import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency, INVOICE_STATUS } from "@/lib/portalConstants";
import { useAuth } from "@/context/AuthContext";
import { format, parseISO } from "date-fns";
import { Loader2, HeartHandshake, FileText, CreditCard, ClipboardList } from "lucide-react";
import { NotesThread } from "@/components/portal/NotesThread";

export default function FamilyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/family/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>;

  const client = data?.client;
  const invoices = data?.invoices || [];
  const careLogs = data?.care_logs || [];

  const payInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/payments/invoice/${inv.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please contact Nicole.");
    }
  };

  return (
    <div className="space-y-8" data-testid="family-dashboard">
      <div>
        <p className="eyebrow mb-2">Care Overview</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal">Welcome, {user?.name}.</h1>
        {client && <p className="text-charcoal-soft mt-2">Care records & billing for <span className="text-charcoal font-medium">{client.name}</span>.</p>}
      </div>

      <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6 flex items-center gap-4">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage/15 text-sage-700"><HeartHandshake className="h-6 w-6" /></span>
        <div>
          <p className="text-sm text-charcoal-soft">Your caregiver</p>
          <p className="font-serif text-xl text-charcoal">Nicole · Certified Nursing Assistant</p>
        </div>
      </div>

      {/* Notes with Nicole — two-way */}
      <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6" data-testid="family-notes-section">
        <h2 className="font-serif text-2xl text-charcoal mb-1">Notes with Nicole</h2>
        <p className="text-sm text-charcoal-soft mb-4">Questions, updates, anything on your mind — she'll see it the moment she logs in.</p>
        <NotesThread clientId={client?.id} mode="family" />
      </div>

      <div>
        <h2 className="font-serif text-2xl text-charcoal mb-4 flex items-center gap-2"><ClipboardList className="h-5 w-5 text-sage-700" /> Recent Care Updates</h2>
        {careLogs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-10 text-center">
            <p className="text-charcoal-soft">Care updates from Nicole will appear here after each visit.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {careLogs.slice(0, 10).map((l) => (
              <div key={l.id} data-testid={`family-log-${l.id}`} className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-5">
                <p className="text-sm text-charcoal-soft">{format(parseISO(l.date), "EEEE, MMMM d, yyyy")}</p>
                {l.summary && <p className="text-charcoal mt-1">"{l.summary}"</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {[...(l.personal_care || []), ...(l.assisted_with || [])].map((t) => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-sage/15 text-sage-700">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif text-2xl text-charcoal mb-4">Itemized Services & Billing</h2>
        {invoices.length === 0 ? (
          <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
            <FileText className="h-10 w-10 text-sage/50 mx-auto mb-4" />
            <p className="text-charcoal-soft">No invoices to show yet. Nicole will share them here as care is provided.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {invoices.map((inv) => {
              const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.sent;
              return (
                <div key={inv.id} data-testid={`family-invoice-${inv.id}`} className="bg-white rounded-2xl border border-charcoal/5 soft-shadow overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-charcoal/5 bg-sand-deep/40">
                    <div>
                      <p className="font-medium text-charcoal">{inv.invoice_number}</p>
                      <p className="text-sm text-charcoal-soft">Issued {inv.issue_date}{inv.due_date && ` · Due ${inv.due_date}`}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                      {inv.status !== "paid" && (
                        <button onClick={() => payInvoice(inv)} data-testid={`pay-${inv.id}`} className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-4 py-2 text-sm font-medium hover:bg-sage-dark transition-colors">
                          <CreditCard className="h-4 w-4" /> Pay online
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="px-6 py-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-charcoal-soft text-xs uppercase tracking-wide">
                          <th className="text-left font-medium pb-2">Service</th>
                          <th className="text-center font-medium pb-2">Qty</th>
                          <th className="text-right font-medium pb-2">Rate</th>
                          <th className="text-right font-medium pb-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-charcoal/5">
                        {inv.line_items.map((li, i) => (
                          <tr key={i}>
                            <td className="py-2.5 text-charcoal">{li.description}</td>
                            <td className="py-2.5 text-center text-charcoal-soft">{li.quantity} {li.unit}</td>
                            <td className="py-2.5 text-right text-charcoal-soft">{currency(li.rate)}</td>
                            <td className="py-2.5 text-right text-charcoal">{currency(li.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 ml-auto w-56 space-y-1">
                      <div className="flex justify-between text-sm text-charcoal-soft"><span>Subtotal</span><span>{currency(inv.subtotal)}</span></div>
                      {inv.tax_rate ? <div className="flex justify-between text-sm text-charcoal-soft"><span>Tax ({inv.tax_rate}%)</span><span>{currency(inv.tax_amount)}</span></div> : null}
                      <div className="flex justify-between font-serif text-xl text-charcoal border-t border-charcoal/10 pt-2"><span>Total</span><span>{currency(inv.total)}</span></div>
                    </div>
                    {inv.notes && <p className="text-sm text-charcoal-soft italic mt-4">{inv.notes}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
